import type {
  ProposalAnalysisResponse,
  ProposalAnalysisStatusResponse,
  ProposalNaturezaResponse,
  ProposalResponse,
  ProposalStatusResponse,
  ProposalStepResponse,
  RuleOriginResponse,
  RulePatchPayload,
  RuleActionResponse,
  LeituraResponse,
  RuleResponse,
  RuleStatusResponse,
} from 'services/actions/ai_management_proposal';
import type { BidderFileCategory } from 'services/actions/ai_management_files';

export type ProposalStatus = ProposalStatusResponse;
export type ProposalStep = ProposalStepResponse;
export type ProposalAnalysisStatus = ProposalAnalysisStatusResponse;
export type ProposalNatureza = ProposalNaturezaResponse;
export type RuleStatus = RuleStatusResponse;
export type RuleOrigin = RuleOriginResponse;
export type RuleAction = RuleActionResponse;
export type Leitura = LeituraResponse;
export type Rule = RuleResponse;

export interface ProposalAnalysis {
  status: ProposalAnalysisStatus;
  rules: Rule[];
  warnings: string[];
  modelFound: boolean | null;
  natureza: ProposalNatureza | null;
}

export interface ProposalGeneratedFile {
  id: string;
  filename: string;
  category: BidderFileCategory | null;
  categoryLabel: string | null;
  downloadUrl: string;
}

export interface Proposal {
  status: ProposalStatus;
  step: ProposalStep;
  analysis: ProposalAnalysis | null;
  rules: Rule[];
  rulesConfirmed: boolean;
  analyzedAt: string | null;
  pendencies: Partial<Record<ProposalStep, number>>;
  generatedAt: string | null;
  generatedByName: string | null;
  generatedFiles: ProposalGeneratedFile[];
}

export const PROPOSAL_STATUS_LABEL: Record<ProposalStatus, string> = {
  nao_iniciada: 'Não iniciada',
  rascunho: 'Rascunho',
  revisada: 'Revisada',
  gerada: 'Gerada',
};

// Qualquer falha da análise (indisponível, consulta falhou, tempo esgotado) chega ao cliente com a
// mesma mensagem: a saída é sempre analisar de novo ou seguir sem o modelo do edital.
export const PROPOSAL_ANALYSIS_ERROR = 'Não foi possível buscar o modelo de proposta no edital.';

const ANALYSIS_STATUSES: ProposalAnalysisStatus[] = ['processing', 'partial', 'done', 'error'];
const NATUREZAS: ProposalNatureza[] = ['bens', 'servico', 'obra', 'indefinida'];
const STATUSES: ProposalStatus[] = ['nao_iniciada', 'rascunho', 'revisada', 'gerada'];
export const PROPOSAL_STEPS: ProposalStep[] = [
  'regras',
  'itens',
  'empresa',
  'declaracoes',
  'revisao',
];

const toAnalysis = (
  analysis: ProposalAnalysisResponse | null | undefined,
): ProposalAnalysis | null => {
  if (!analysis || !ANALYSIS_STATUSES.includes(analysis.status)) return null;

  const modelo = analysis.modelo as { encontrado?: unknown } | null | undefined;
  return {
    status: analysis.status,
    rules: Array.isArray(analysis.regras) ? analysis.regras : [],
    warnings: Array.isArray(analysis.avisos) ? analysis.avisos : [],
    modelFound: typeof modelo?.encontrado === 'boolean' ? modelo.encontrado : null,
    natureza: analysis.natureza && NATUREZAS.includes(analysis.natureza) ? analysis.natureza : null,
  };
};

const toPendencies = (pendencias: ProposalResponse['pendencias']) =>
  PROPOSAL_STEPS.reduce<Partial<Record<ProposalStep, number>>>((acc, step) => {
    const count = pendencias?.[step];
    return typeof count === 'number' && count >= 0 ? { ...acc, [step]: count } : acc;
  }, {});

export const toProposal = (data: ProposalResponse): Proposal => ({
  status: STATUSES.includes(data.status) ? data.status : 'nao_iniciada',
  step: PROPOSAL_STEPS.includes(data.etapa_atual) ? data.etapa_atual : 'regras',
  analysis: toAnalysis(data.analysis),
  rules: Array.isArray(data.rules) ? data.rules : [],
  rulesConfirmed: !!data.rules_confirmed,
  analyzedAt: typeof data.analyzed_at === 'string' ? data.analyzed_at : null,
  pendencies: toPendencies(data.pendencias),
  generatedAt: typeof data.generated_at === 'string' ? data.generated_at : null,
  generatedByName: data.generated_by_name ?? null,
  generatedFiles: (Array.isArray(data.generated_files) ? data.generated_files : []).map((file) => ({
    id: file.id,
    filename: file.filename,
    category: file.category ?? null,
    categoryLabel: file.category_label ?? null,
    downloadUrl: file.download_url,
  })),
});

export const PROPOSAL_ANALYSIS_TIMED_OUT: ProposalAnalysis = {
  status: 'error',
  rules: [],
  warnings: [],
  modelFound: null,
  natureza: null,
};

// Nome da etapa 1 ainda em confirmação ("Regras do edital" ou "Exigências"): trocar só aqui.
export const RULES_STEP_LABEL = 'Regras do edital';

export const proposalStepLabel = (step: ProposalStep, editalHasRules = true) =>
  ({
    regras: RULES_STEP_LABEL,
    itens: 'Itens e preços',
    empresa: 'Empresa e condições',
    declaracoes: 'Declarações e documentos',
    revisao: editalHasRules ? 'Revisão e checagem' : 'Revisão',
  }[step]);

export const RULES_LOCK_MESSAGE = `Valide as ${RULES_STEP_LABEL.toLowerCase()} para seguir`;

export const RULE_STATUSES: RuleStatus[] = ['a_validar', 'valida', 'nao_se_aplica'];

export const RULE_STATUS_LABEL: Record<RuleStatus, string> = {
  a_validar: 'A validar',
  valida: 'Válida',
  nao_se_aplica: 'Não se aplica',
};

// Marca/modelo cita os três (inclui fabricante). Tipo fora da lista cai no próprio nome da regra.
export const RULE_TYPE_LABEL: Record<string, string> = {
  validade_proposta: 'Validade da proposta',
  prazo_entrega: 'Prazo de entrega',
  prazo_execucao: 'Prazo de execução',
  marca_modelo_fabricante: 'Marca, modelo e fabricante',
  casas_decimais: 'Casas decimais',
  valor_extenso: 'Valor por extenso',
  declaracoes: 'Declarações',
  garantia_proposta: 'Garantia de proposta',
  documentos_complementares: 'Documentos complementares',
  assinatura: 'Assinatura',
  lote_ou_item: 'Proposta por lote ou por item',
};

export const ruleStatusOf = (rule: Rule): RuleStatus =>
  rule.status && RULE_STATUSES.includes(rule.status) ? rule.status : 'a_validar';

// Texto puro: o React escapa na renderização (por segurança: o valor vem de editais).
export const ruleTitle = (rule: Rule): string => {
  const name = RULE_TYPE_LABEL[rule.regra] ?? rule.regra;
  const value = typeof rule.valor === 'string' ? rule.valor.trim() : '';
  return value ? `${name}: ${value}` : name;
};

// A citação que a regra mostra entre aspas: o trecho do edital; para a garantia (origem resumo, sem
// trecho) é a justificativa do resumo (campo valor). Null quando não há nada a citar.
export const ruleQuote = (rule: Rule): string | null => {
  const trecho = typeof rule.trecho === 'string' ? rule.trecho.trim() : '';
  if (trecho) return trecho;
  if (rule.origem === 'resumo') {
    const valor = typeof rule.valor === 'string' ? rule.valor.trim() : '';
    if (valor) return valor;
  }
  return null;
};

// A regra do cliente aponta onde conferir em vez de um trecho do edital.
export const ruleWhereToCheck = (rule: Rule): string | null => {
  const onde = typeof rule.onde_conferir === 'string' ? rule.onde_conferir.trim() : '';
  return onde || null;
};

// Só os campos que o back aceita do front; acao/leitura_escolhida vão só nas controversas e são
// preservados nas regravações.
export const toRulePatch = (
  rule: Rule,
  status: RuleStatus,
  motivoNaoSeAplica?: string | null,
): RulePatchPayload => {
  const motivo = status === 'nao_se_aplica' ? motivoNaoSeAplica?.trim() || null : null;
  return {
    regra: rule.regra,
    origem: rule.origem ?? null,
    status,
    exige: typeof rule.exige === 'boolean' ? rule.exige : null,
    valor: typeof rule.valor === 'string' ? rule.valor : null,
    trecho: typeof rule.trecho === 'string' ? rule.trecho : null,
    arquivo: typeof rule.arquivo === 'string' ? rule.arquivo : null,
    pagina: typeof rule.pagina === 'number' ? rule.pagina : null,
    clausula: typeof rule.clausula === 'string' ? rule.clausula : null,
    motivo_nao_se_aplica: motivo,
    // onde_conferir é obrigatório no back para a regra do cliente: reenviar sem ele faz o PATCH
    // inteiro falhar quando a lista traz uma regra do cliente já gravada.
    onde_conferir: typeof rule.onde_conferir === 'string' ? rule.onde_conferir : null,
    valor_extraido: typeof rule.valor_extraido === 'string' ? rule.valor_extraido : null,
    // Preservados para que regravar a lista inteira não apague a ação/leitura já escolhidas numa
    // regra controversa.
    acao:
      rule.acao === 'impugnar' ||
      rule.acao === 'esclarecer' ||
      rule.acao === 'assumir_risco' ||
      rule.acao === 'resolvida_no_planejamento'
        ? rule.acao
        : null,
    leitura_escolhida: typeof rule.leitura_escolhida === 'number' ? rule.leitura_escolhida : null,
  };
};

// Só estas três regras numéricas aceitam ajuste do valor; o back recusa (422) o ajuste de outras.
export const NUMERIC_RULES = ['validade_proposta', 'prazo_entrega', 'casas_decimais'];

export const CLIENT_RULE_TEXT_LIMIT = 200;

export const isNumericRule = (rule: Rule): boolean => NUMERIC_RULES.includes(rule.regra);

export const isAdjustedRule = (rule: Rule): boolean => rule.origem === 'ajustada';

export const isClientRule = (rule: Rule): boolean => rule.origem === 'cliente';

export const ruleExtractedValue = (rule: Rule): string | null =>
  typeof rule.valor_extraido === 'string' ? rule.valor_extraido : null;

// valor_extraido (o que a análise leu) é obrigatório no back para a origem "ajustada".
export const toAdjustedRulePatch = (
  rule: Rule,
  novoValor: string,
  valorExtraido: string,
): RulePatchPayload => ({
  ...toRulePatch(rule, ruleStatusOf(rule)),
  origem: 'ajustada',
  valor: novoValor,
  valor_extraido: valorExtraido,
});

export const toRevertedRulePatch = (
  rule: Rule,
  origemOriginal: RuleOrigin,
  valorExtraido: string,
): RulePatchPayload => ({
  ...toRulePatch(rule, ruleStatusOf(rule)),
  origem: origemOriginal,
  valor: valorExtraido,
  valor_extraido: null,
});

export const toClientRulePatch = (
  texto: string,
  ondeConferir: string,
  trecho?: string | null,
): RulePatchPayload => ({
  regra: texto.trim(),
  origem: 'cliente',
  status: 'a_validar',
  onde_conferir: ondeConferir.trim(),
  trecho: trecho?.trim() || null,
});

export const isControversialRule = (rule: Rule): boolean => rule.controversa === true;

export const ruleReadings = (rule: Rule): Leitura[] =>
  Array.isArray(rule.leituras) ? rule.leituras : [];

export const ruleChosenReading = (rule: Rule): number | null =>
  typeof rule.leitura_escolhida === 'number' ? rule.leitura_escolhida : null;

export const ruleAction = (rule: Rule): RuleAction | null =>
  rule.acao === 'impugnar' ||
  rule.acao === 'esclarecer' ||
  rule.acao === 'assumir_risco' ||
  rule.acao === 'resolvida_no_planejamento'
    ? rule.acao
    : null;

// Impugnar/esclarecer mantêm a regra "A validar" sem escolher leitura; assumir o risco e resolvida
// no planejamento validam a regra com a leitura escolhida.
export const toControversialRulePatch = (
  rule: Rule,
  acao: RuleAction,
  leituraEscolhida?: number | null,
): RulePatchPayload => {
  const valida = acao === 'assumir_risco' || acao === 'resolvida_no_planejamento';
  return {
    ...toRulePatch(rule, valida ? 'valida' : 'a_validar'),
    acao,
    leitura_escolhida: valida && typeof leituraEscolhida === 'number' ? leituraEscolhida : null,
  };
};

// Alvos do "Corrigir" da checagem: o data-proposal-target que cada etapa põe no elemento.
export const proposalItemTarget = (
  lote: number | null | undefined,
  item: number | null | undefined,
) => `item-${lote ?? ''}-${item ?? ''}`;
export const proposalRuleTarget = (regra: string) => `regra-${regra}`;
export const proposalRequirementTarget = (id: string) => `exigencia-${id}`;
export const proposalFieldTarget = (campo: string) => `campo-${campo}`;
